import { useEffect, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { api } from "../lib/api";
import { useAuth } from "../contexts/AuthContext";
import { Button } from "../components/ui/button";

const MAX_ATTEMPTS = 8;
const POLL_MS = 2000;

export default function PaymentSuccess() {
  const { t } = useTranslation();
  const [search] = useSearchParams();
  const navigate = useNavigate();
  const { setUser } = useAuth();
  const [state, setState] = useState("pending"); // pending | success | failed | timeout
  const attemptsRef = useRef(0);
  const sessionId = search.get("session_id");

  useEffect(() => {
    if (!sessionId) {
      setState("failed");
      return;
    }
    let cancelled = false;
    let timer;

    const poll = async () => {
      attemptsRef.current += 1;
      try {
        const { data } = await api.get(`/billing/checkout/status/${sessionId}`);
        if (cancelled) return;
        if (data.payment_status === "paid") {
          setState("success");
          // refresh user state
          try {
            const me = await api.get("/auth/me");
            if (!cancelled) setUser(me.data);
          } catch {}
          return;
        }
        if (data.status === "expired" || data.payment_status === "failed") {
          setState("failed");
          return;
        }
        if (attemptsRef.current >= MAX_ATTEMPTS) {
          setState("timeout");
          return;
        }
        timer = setTimeout(poll, POLL_MS);
      } catch {
        if (cancelled) return;
        if (attemptsRef.current >= MAX_ATTEMPTS) setState("timeout");
        else timer = setTimeout(poll, POLL_MS);
      }
    };
    poll();
    return () => {
      cancelled = true;
      if (timer) clearTimeout(timer);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionId]);

  return (
    <div className="min-h-[calc(100vh-4rem)] grid place-items-center bg-[#FDFBF7] px-6" data-testid="payment-success-page">
      <div className="max-w-md w-full border-2 border-[#1C1917] bg-white p-10 shadow-[6px_6px_0_0_#1C1917] text-center">
        {state === "pending" && (
          <>
            <div className="mx-auto w-16 h-16 border-4 border-[#1C1917] border-t-[#E5484D] rounded-full animate-spin" />
            <h1 className="mt-6 hero-h2 text-2xl text-[#1C1917]">{t("payment_pending_title")}</h1>
            <p className="mt-2 text-sm text-[#57534E]">{t("payment_pending_sub")}</p>
          </>
        )}
        {state === "success" && (
          <>
            <div className="mx-auto w-16 h-16 grid place-items-center bg-[#46A758] border-4 border-[#1C1917] text-white text-3xl font-black">
              ✓
            </div>
            <h1 className="mt-6 hero-h2 text-2xl text-[#1C1917]">{t("payment_success_title")}</h1>
            <p className="mt-2 text-sm text-[#57534E]">{t("payment_success_sub")}</p>
            <Button
              onClick={() => navigate("/dashboard")}
              className="mt-6 w-full rounded-none border-2 border-[#1C1917] bg-[#1C1917] text-[#FDFBF7] hover:bg-black font-black shadow-[3px_3px_0_0_#1C1917] hover:shadow-[5px_5px_0_0_#1C1917]"
              data-testid="btn-back-dashboard"
            >
              {t("payment_back_dashboard")}
            </Button>
          </>
        )}
        {(state === "failed" || state === "timeout") && (
          <>
            <div className="mx-auto w-16 h-16 grid place-items-center bg-[#E5484D] border-4 border-[#1C1917] text-white text-3xl font-black">
              !
            </div>
            <h1 className="mt-6 hero-h2 text-2xl text-[#1C1917]">{t("payment_failed_title")}</h1>
            <p className="mt-2 text-sm text-[#57534E]">{t("payment_failed_sub")}</p>
            <Button
              onClick={() => navigate("/pricing")}
              className="mt-6 w-full rounded-none border-2 border-[#1C1917] bg-[#E5484D] text-white hover:bg-[#CE2C31] font-black shadow-[3px_3px_0_0_#1C1917] hover:shadow-[5px_5px_0_0_#1C1917]"
              data-testid="btn-retry-pricing"
            >
              {t("paywall_cta")}
            </Button>
          </>
        )}
      </div>
    </div>
  );
}
